/**
 * TvShell — marco común de las pantallas de monitoreo en televisor.
 *
 * Ocupa la ventana completa (sin la barra lateral del portal: en un televisor
 * la navegación no sirve para nada y roba espacio) y aporta lo único que se
 * repite entre pantallas: cabecera con estado, indicador de la vista en curso,
 * y un panel de ajustes que se abre al mover el ratón y se esconde solo.
 *
 * El panel se OCULTA por sí mismo porque estas pantallas se dejan encendidas:
 * cualquier control permanente acabaría formando parte del paisaje, y en un
 * televisor cada píxel de adorno es un dato que no se ve.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Maximize2, Minimize2, Moon, Pause, Play, RefreshCw, Settings2, Sun, WifiOff,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { INTERVALOS_REFRESCO, INTERVALOS_ROTACION, type EstadoTV } from '../../hooks/useModoTV';
import styles from './TvShell.module.css';

interface Props {
  titulo: string;
  /** Etiquetas visibles de cada vista, en el mismo orden que en useModoTV. */
  vistas: readonly string[];
  tv: EstadoTV;
  /** Franja de contexto a la derecha del título (filtros, sala, etc.). */
  contexto?: ReactNode;
  children: ReactNode;
}

const hora = (d: Date) =>
  new Intl.DateTimeFormat('es-PA', { hour: '2-digit', minute: '2-digit', hour12: true }).format(d);

export function TvShell({ titulo, vistas, tv, contexto, children }: Props) {
  const [ajustesVisibles, setAjustesVisibles] = useState(false);
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [reloj, setReloj] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setReloj(new Date()), 20_000);
    return () => clearInterval(id);
  }, []);

  // Los controles aparecen con el movimiento del ratón y se van a los 6 s.
  useEffect(() => {
    const despertar = () => {
      setAjustesVisibles(true);
      if (temporizador.current) clearTimeout(temporizador.current);
      temporizador.current = setTimeout(() => setAjustesVisibles(false), 6000);
    };
    window.addEventListener('mousemove', despertar);
    window.addEventListener('keydown', despertar);
    return () => {
      window.removeEventListener('mousemove', despertar);
      window.removeEventListener('keydown', despertar);
      if (temporizador.current) clearTimeout(temporizador.current);
    };
  }, []);

  return (
    <div className={`${styles.pantalla} ${tv.temaEfectivo === 'claro' ? styles.claro : ''}`}>
      <header className={styles.cabecera}>
        <div className={styles.identidad}>
          <span className={styles.marca}>MINSAlud PdP</span>
          <span className={styles.filete} aria-hidden="true" />
          <h1 className={styles.titulo}>{titulo}</h1>
        </div>

        {contexto && <div className={styles.contexto}>{contexto}</div>}

        <div className={styles.estado}>
          {!tv.enLinea && (
            <span className={styles.sinRed}>
              <WifiOff size={16} /> Sin conexión
            </span>
          )}
          <span className={styles.actualizado}>
            Actualizado {hora(tv.ultimoRefresco)}
          </span>
          <span className={styles.reloj}>{hora(reloj)}</span>
        </div>
      </header>

      {/* Pestañas de vista: el punto lleno indica dónde está y la barra cuánto
          falta para el cambio. Sin esto, el televisor cambia de contenido sin
          avisar y quien mira pierde el hilo. */}
      <nav className={styles.pasos} aria-label="Vistas del tablero">
        {vistas.map((v, i) => (
          <button
            key={v}
            type="button"
            className={`${styles.paso} ${i === tv.vistaActual ? styles.pasoActivo : ''}`}
            onClick={() => tv.irA(i)}
          >
            <span className={styles.pasoTexto}>{v}</span>
            {i === tv.vistaActual && tv.rotando && (
              <span className={styles.pasoProgreso} style={{ width: `${tv.progresoVista * 100}%` }} />
            )}
          </button>
        ))}
      </nav>

      <main className={styles.lienzo}>{children}</main>

      <div className={`${styles.ajustes} ${ajustesVisibles ? styles.ajustesVisibles : ''}`}>
        <button type="button" className={styles.btnIcono} onClick={() => tv.irA(tv.vistaActual - 1)} title="Vista anterior (←)">
          <ChevronLeft size={18} />
        </button>
        <button
          type="button"
          className={styles.btnIcono}
          onClick={() => tv.setRotando(!tv.rotando)}
          title={tv.rotando ? 'Pausar rotación (espacio)' : 'Reanudar rotación (espacio)'}
        >
          {tv.rotando ? <Pause size={18} /> : <Play size={18} />}
        </button>
        <button type="button" className={styles.btnIcono} onClick={() => tv.irA(tv.vistaActual + 1)} title="Vista siguiente (→)">
          <ChevronRight size={18} />
        </button>

        <span className={styles.separador} />

        <label className={styles.campo}>
          <RefreshCw size={14} /> Refresco
          <select
            value={tv.refrescoMs}
            onChange={(e) => tv.setRefrescoMs(Number(e.target.value))}
            className={styles.select}
          >
            {INTERVALOS_REFRESCO.map((o) => (
              <option key={o.ms} value={o.ms}>{o.etiqueta}</option>
            ))}
          </select>
        </label>

        <label className={styles.campo}>
          <Settings2 size={14} /> Cambio de vista
          <select
            value={tv.rotacionMs}
            onChange={(e) => tv.setRotacionMs(Number(e.target.value))}
            className={styles.select}
          >
            {INTERVALOS_ROTACION.map((o) => (
              <option key={o.ms} value={o.ms}>{o.etiqueta}</option>
            ))}
          </select>
        </label>

        <span className={styles.separador} />

        <button
          type="button"
          className={styles.btnIcono}
          onClick={() => tv.setTema(tv.temaEfectivo === 'oscuro' ? 'claro' : 'oscuro')}
          title={tv.temaEfectivo === 'oscuro' ? 'Cambiar a tema claro (T)' : 'Cambiar a tema oscuro (T)'}
          aria-label="Cambiar tema"
        >
          {tv.temaEfectivo === 'oscuro' ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        <button type="button" className={styles.btnIcono} onClick={tv.refrescarAhora} title="Refrescar ahora (R)">
          <RefreshCw size={18} />
        </button>
        <button type="button" className={styles.btnIcono} onClick={tv.alternarPantallaCompleta} title="Pantalla completa (F)">
          {tv.pantallaCompleta ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
        </button>
      </div>
    </div>
  );
}
